import { useEffect, useState } from 'react'
import {X, Upload, User as UserIcon, Crown, ShieldCheck, Building2, MapPin, Check} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { maskCPF, maskPhone, unmaskCPF, unmaskPhone, isValidCPF, hashPassword, nowISO } from '@/utils/masks'

interface Cidade { _id: string, nome: string }
interface Secretaria { _id: string, nome: string, cidadeId: string }
interface SubLocal { _id: string, nome: string, secretariaId: string, cidadeId: string }

const FUNCOES_INFO = [
  {
    role: 'ADMIN',
    label: 'Administrador',
    Icon: Crown,
    resumo: 'Acesso total ao sistema',
    permissoes: [
      'Todas as cidades, secretarias e sub locais',
      'Criar, editar e excluir utilizadores',
      'Analisar, aprovar e reprovar qualquer requerimento',
      'Gestão de cidades, secretarias e configurações',
    ],
  },
  {
    role: 'MODERADOR',
    label: 'Moderador',
    Icon: ShieldCheck,
    resumo: 'Toda a sua cidade',
    permissoes: [
      'Todas as secretarias e sub locais da cidade',
      'Analisar, aprovar e reprovar requerimentos',
      'Ver e restaurar requerimentos excluídos',
      'Gerir utilizadores da cidade',
    ],
  },
  {
    role: 'GESTOR',
    label: 'Gestor',
    Icon: Building2,
    resumo: 'Uma secretaria',
    permissoes: [
      'Requerimentos da secretaria atribuída',
      'Todos os sub locais dessa secretaria',
      'Criar e acompanhar requerimentos',
      'Sem permissão para aprovar ou reprovar',
    ],
  },
  {
    role: 'COORDENADOR',
    label: 'Coordenador',
    Icon: MapPin,
    resumo: 'Sub locais atribuídos',
    permissoes: [
      'Requerimentos dos sub locais atribuídos',
      'Criar requerimentos nesses sub locais',
      'Acompanhar o estado dos pedidos',
      'Sem permissão para aprovar ou reprovar',
    ],
  },
]

interface UsuarioModalProps {
  usuario?: any
  cidades: Cidade[]
  secretarias: Secretaria[]
  subLocais?: SubLocal[]
  onClose: () => void
  onSaved: () => void
}

function UsuarioModal({ usuario, cidades, secretarias, subLocais = [], onClose, onSaved }: UsuarioModalProps) {
  const { user: currentUser } = useAppAuth()
  const isEdit = !!usuario
  const isModeradorLogado = currentUser?.role === 'MODERADOR'
  const cidadesDisponiveis = isModeradorLogado ? cidades.filter(c => c._id === currentUser?.cidadeId) : cidades
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [nomeCompleto, setNomeCompleto] = useState(usuario?.nomeCompleto || '')
  const [cpf, setCpf] = useState(usuario ? maskCPF(usuario.cpf) : '')
  const [telefone, setTelefone] = useState(usuario ? maskPhone(usuario.telefone || '') : '')
  const [email, setEmail] = useState(usuario?.email || '')
  const [funcao, setFuncao] = useState(usuario?.funcao || '')
  const [matricula, setMatricula] = useState(usuario?.matricula || '')
  const [role, setRole] = useState(usuario?.role || 'GESTOR')
  const isAdminSelecionado = role === 'ADMIN'
  const [cidadeId, setCidadeId] = useState(usuario?.cidadeId || (usuario?.role === 'ADMIN' ? 'TODAS' : (isModeradorLogado ? currentUser?.cidadeId || '' : '')))
  const [secretariaId, setSecretariaId] = useState(usuario?.secretariaId || '')
  const [subLocalIds, setSubLocalIds] = useState<string[]>(usuario?.subLocalIds || [])
  const [status, setStatus] = useState(usuario?.status || 'ativo')
  const [fotoUrl, setFotoUrl] = useState(usuario?.fotoUrl || '')
  const [senha, setSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})

  const isAdminLogado = currentUser?.role === 'ADMIN'
  const funcaoAtual = FUNCOES_INFO.find(f => f.role === role) || FUNCOES_INFO[2]
  const secretariasDaCidade = secretarias.filter(s => s.cidadeId === cidadeId)
  const subLocaisDaSecretaria = subLocais.filter(s => s.secretariaId === secretariaId)
  const isCoordenadorSelecionado = role === 'COORDENADOR'

  useEffect(() => {
    if (!secretariasDaCidade.some(s => s._id === secretariaId))
      setSecretariaId('')
  }, [cidadeId])

  useEffect(() => {
    setSubLocalIds(prev => prev.filter(id => subLocaisDaSecretaria.some(s => s._id === id)))
  }, [secretariaId])

  useEffect(() => {
    if (isAdminSelecionado) {
      setCidadeId('TODAS')
      setSecretariaId('')
    }
    else if (cidadeId === 'TODAS') {
      setCidadeId(isModeradorLogado ? currentUser?.cidadeId || '' : '')
    }
  }, [role])

  const handleUploadFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results?.[0]
      if (result?.fileUrl) {
        setFotoUrl(result.fileUrl)
      }
      else {
        toast.error(result?.uploadError || 'Erro ao enviar a foto.')
      }
    }
    catch {
      toast.error('Erro ao enviar a foto.')
    }
    finally {
      setUploading(false)
    }
  }

  const validar = () => {
    const novosErros: Record<string, string> = {}
    if (!nomeCompleto.trim()) novosErros.nomeCompleto = 'Nome obrigatório.'
    if (!isValidCPF(cpf)) novosErros.cpf = 'CPF inválido.'
    if (!isAdminSelecionado && !cidadeId) novosErros.cidadeId = 'Selecione uma cidade.'
    if ((role === 'GESTOR' || role === 'COORDENADOR') && !secretariaId) novosErros.secretariaId = 'Secretaria obrigatória para Gestor e Coordenador.'
    if (role === 'COORDENADOR' && subLocalIds.length === 0) novosErros.subLocalIds = 'Selecione ao menos um sub local para o Coordenador.'
    if (!isEdit && senha.length < 6) novosErros.senha = 'A senha deve ter no mínimo 6 caracteres.'
    if (!isEdit && senha !== confirmarSenha) novosErros.confirmarSenha = 'As senhas não coincidem.'
    if (isEdit && senha && senha !== confirmarSenha) novosErros.confirmarSenha = 'As senhas não coincidem.'
    if (isEdit && senha && senha.length < 6) novosErros.senha = 'A senha deve ter no mínimo 6 caracteres.'
    setErros(novosErros)
    return Object.keys(novosErros).length === 0
  }

  const handleSubmit = async () => {
    if (!validar()) return
    setSaving(true)
    try {
      const cpfLimpo = unmaskCPF(cpf)

      if (!isEdit) {
        const existente = await (lumi.entities as any).user_profiles.list({ filter: { cpf: cpfLimpo }, limit: 1 })
        if (existente?.list?.length) {
          setErros({ cpf: 'Já existe um utilizador com este CPF.' })
          setSaving(false)
          return
        }
      }

      const payload: Record<string, any> = {
        nomeCompleto: nomeCompleto.trim(),
        cpf: cpfLimpo,
        telefone: unmaskPhone(telefone),
        email: email.trim(),
        funcao: funcao.trim(),
        matricula: matricula.trim(),
        cidadeId: isAdminSelecionado ? 'TODAS' : cidadeId,
        secretariaId: (role === 'GESTOR' || role === 'COORDENADOR') ? secretariaId : '',
        subLocalIds: role === 'COORDENADOR' ? subLocalIds : [],
        role,
        status,
        fotoUrl,
        updatedAt: nowISO(),
      }

      if (senha) {
        payload.passwordHash = await hashPassword(senha)
      }

      if (isEdit) {
        await (lumi.entities as any).user_profiles.update(usuario._id, payload)
        toast.success('Utilizador atualizado com sucesso.')
      }
      else {
        await (lumi.entities as any).user_profiles.create({
          ...payload,
          userId: `user-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          passwordHash: await hashPassword(senha),
          isSystemAdmin: false,
          creator: currentUser?.userId || 'system',
          createdAt: nowISO(),
        })
        toast.success('Utilizador criado com sucesso.')
      }
      onSaved()
      onClose()
    }
    catch {
      toast.error('Erro ao guardar utilizador. Tente novamente.')
    }
    finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-[640px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7E9EC] sticky top-0 bg-white z-10">
          <h2 className="text-[16px] font-semibold text-[#12141A]">
            {isEdit ? 'Editar Utilizador' : 'Novo Utilizador'}
          </h2>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#F1F2F4] flex items-center justify-center overflow-hidden shrink-0">
              {fotoUrl ? <img src={fotoUrl} alt="Foto" className="w-full h-full object-cover" /> : <UserIcon className="w-7 h-7 text-[#9CA3AF]" strokeWidth={1.5} />}
            </div>
            <label className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-[#D1D5DB] text-[13px] font-medium text-[#374151] hover:bg-[#F5F6F8] cursor-pointer">
              <Upload className="w-4 h-4" strokeWidth={1.75} />
              {uploading ? 'A enviar...' : 'Carregar foto'}
              <input type="file" accept="image/*" className="hidden" onChange={handleUploadFoto} disabled={uploading} />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nome Completo</label>
              <input value={nomeCompleto} onChange={e => setNomeCompleto(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
              {erros.nomeCompleto && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.nomeCompleto}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">CPF</label>
              <input value={cpf} onChange={e => setCpf(maskCPF(e.target.value))} maxLength={14} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
              {erros.cpf && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.cpf}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Telefone (WhatsApp)</label>
              <input value={telefone} onChange={e => setTelefone(maskPhone(e.target.value))} maxLength={15} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">E-mail (apenas cadastro)</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Função</label>
              <input value={funcao} onChange={e => setFuncao(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Matrícula (opcional)</label>
              <input value={matricula} onChange={e => setMatricula(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Cidade</label>
              <select
                value={isAdminSelecionado ? 'TODAS' : cidadeId}
                onChange={e => setCidadeId(e.target.value)}
                disabled={isModeradorLogado || isAdminSelecionado}
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white disabled:bg-[#F7F8FA] disabled:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              >
                {isAdminSelecionado
                  ? <option value="TODAS">Todas as cidades</option>
                  : (
                      <>
                        <option value="">Selecione...</option>
                        {cidadesDisponiveis.map(c => <option key={c._id} value={c._id}>{c.nome}</option>)}
                      </>
                    )}
              </select>
              {isAdminSelecionado && (
                <p className="text-[12px] text-[#9CA3AF] mt-1">Administrador tem acesso a todas as cidades.</p>
              )}
              {erros.cidadeId && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.cidadeId}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                Secretaria {(role === 'GESTOR' || role === 'COORDENADOR') ? '(obrigatório)' : ''}
              </label>
              <select
                value={secretariaId}
                onChange={e => setSecretariaId(e.target.value)}
                disabled={!cidadeId || (role !== 'GESTOR' && role !== 'COORDENADOR')}
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white disabled:bg-[#F7F8FA] disabled:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              >
                <option value="">Selecione...</option>
                {secretariasDaCidade.map(s => <option key={s._id} value={s._id}>{s.nome}</option>)}
              </select>
              {erros.secretariaId && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.secretariaId}</p>}
              {role === 'GESTOR' && (
                <p className="text-[12px] text-[#9CA3AF] mt-1">Gestor terá acesso automático a todos os sub locais desta secretaria.</p>
              )}
            </div>

            <div className={isAdminLogado ? 'sm:col-span-2' : ''}>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nível de Acesso</label>
              {isAdminLogado ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {FUNCOES_INFO.map(f => {
                      const ativo = role === f.role
                      return (
                        <button
                          key={f.role}
                          type="button"
                          onClick={() => setRole(f.role)}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${ativo ? 'border-[#1E4FBF] bg-[#F2F6FE]' : 'border-[#E1E4E8] bg-white hover:bg-[#F7F8FA]'}`}
                        >
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${ativo ? 'bg-[#1E4FBF] text-white' : 'bg-[#F1F2F4] text-[#6B7280]'}`}>
                            <f.Icon className="w-4 h-4" strokeWidth={1.9} />
                          </span>
                          <span className="min-w-0">
                            <span className={`block text-[13.5px] font-semibold ${ativo ? 'text-[#12387F]' : 'text-[#12141A]'}`}>{f.label}</span>
                            <span className="block text-[11.5px] text-[#6B7280] truncate">{f.resumo}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>

                  <div className="mt-3 rounded-xl border border-[#E1E4E8] bg-[#FAFBFC] p-3.5">
                    <div className="flex items-center gap-2 mb-2">
                      <funcaoAtual.Icon className="w-3.5 h-3.5 text-[#1E4FBF]" strokeWidth={2} />
                      <span className="text-[12px] font-semibold text-[#374151]">
                        O que o {funcaoAtual.label} pode acessar
                      </span>
                    </div>
                    <ul className="flex flex-col gap-1.5">
                      {funcaoAtual.permissoes.map(p => (
                        <li key={p} className="flex items-start gap-2 text-[12.5px] text-[#4B5563] leading-snug">
                          <Check className="w-3.5 h-3.5 text-[#1E4FBF] mt-0.5 shrink-0" strokeWidth={2.4} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <select value={role} onChange={e => setRole(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]">
                  <option value="ADMIN">Administrador</option>
                  <option value="MODERADOR">Moderador</option>
                  <option value="GESTOR">Gestor</option>
                  <option value="COORDENADOR">Coordenador</option>
                </select>
              )}
            </div>

            {isCoordenadorSelecionado && (
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Sub Locais Atribuídos (obrigatório)</label>
                {!secretariaId && (
                  <p className="text-[12.5px] text-[#9CA3AF]">Selecione uma secretaria primeiro.</p>
                )}
                {secretariaId && subLocaisDaSecretaria.length === 0 && (
                  <p className="text-[12.5px] text-[#9CA3AF]">Nenhum sub local cadastrado para esta secretaria.</p>
                )}
                {secretariaId && subLocaisDaSecretaria.length > 0 && (
                  <div className="flex flex-col gap-1.5 max-h-[160px] overflow-y-auto border border-[#D1D5DB] rounded-lg p-3">
                    {subLocaisDaSecretaria.map(sl => (
                      <label key={sl._id} className="flex items-center gap-2.5 text-[13.5px] text-[#12141A] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={subLocalIds.includes(sl._id)}
                          onChange={(e) => {
                            setSubLocalIds(prev => e.target.checked ? [...prev, sl._id] : prev.filter(id => id !== sl._id))
                          }}
                          className="accent-[#1E4FBF] w-4 h-4"
                        />
                        {sl.nome}
                      </label>
                    ))}
                  </div>
                )}
                {erros.subLocalIds && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.subLocalIds}</p>}
              </div>
            )}

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Estado</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]">
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                {isEdit ? 'Nova Senha (opcional)' : 'Senha'}
              </label>
              <input type="password" value={senha} onChange={e => setSenha(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
              {erros.senha && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.senha}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Confirmar Senha</label>
              <input type="password" value={confirmarSenha} onChange={e => setConfirmarSenha(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
              {erros.confirmarSenha && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.confirmarSenha}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white disabled:opacity-60"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UsuarioModal
